import { isEmpty, isNull } from "lodash";
import { t } from "@/i18n-js/instance";
import { DIRECT_CHAT_ROOM } from "@/react/components/constants";
import { chatRoomPath, chatSpacePath } from "@circle-react/helpers/urlHelpers";
import { BOTTOM_NAV_HEIGHT, HEADER_HEIGHT } from "@circle-react-uikit/Rail";

export const SCROLL_WRAPPER_DEFAULT_HEIGHT = HEADER_HEIGHT + BOTTOM_NAV_HEIGHT;
export const LIVE_STEAM_HEADER_HEIGHT = 96;
export const COURSES_HEADER_HEIGHT = 112;
export const APP_HEADER_HEIGHT = 64;

export const MAX_MESSAGES_THRESHOLD = 80;
export const MESSAGES_THRESHOLD = 60;

export const getParticipantNames = (participants: any[]) => {
  let participantNames = "";

  participants.forEach((participant, index) => {
    if (index == participants.length - 1) {
      participantNames = participantNames + participant.name + ".";
    } else if (index == participants.length - 2) {
      participantNames = participantNames + participant.name + " and ";
    } else {
      participantNames = participantNames + participant.name + ", ";
    }
  });

  return participantNames;
};

export const isPersistedMessage = (message: any) => !!message.id;

export const messageWithSameUUIDExists = (messages: any[] = [], message: any) =>
  messages.some(
    currentMessage =>
      currentMessage.creation_uuid === message.creation_uuid &&
      (!message.id || !currentMessage.id),
  );

export const scrollToMessage = (id: string, callback?: () => void) => {
  const messageElement = document.getElementById(id);
  const scrollTimeOut = setTimeout(() => {
    messageElement?.scrollIntoView({ behavior: "smooth", block: "center" });
    callback?.();
  }, 100);
  return () => clearTimeout(scrollTimeOut);
};

export const getRole = (
  communityMemberId: string,
  roles: Record<string, any>,
) => {
  for (const [key, value] of Object.entries(roles || [])) {
    if (Array.isArray(value) && value.includes(communityMemberId)) {
      return key;
    }
  }
  return undefined;
};

export const isMobileBottomNavVisible = () => {
  const mobileBottomNav = document.getElementById("mobile-bottom-toolbar");
  return Boolean(mobileBottomNav);
};

export const isBookmarksBarVisible = () => {
  const bookmarksBar = document.getElementById("bookmarks-bar");
  return Boolean(bookmarksBar);
};

export const getCoursesHeaderHeight = () => {
  const helloBar = document.getElementById("lesson-hello-bar");
  return helloBar ? COURSES_HEADER_HEIGHT : HEADER_HEIGHT;
};

export const getHeightWhenMessagingModalIsOpen = (height: number) =>
  height < 20 ? 100 : height;

export const isMessageSent = (message: any, currentParticipant: any) =>
  message.sender.id === currentParticipant.id;

export const isLastMessageSame = (lastMessageInArr?: any, lastMessage?: any) =>
  lastMessageInArr &&
  !!lastMessageInArr.id &&
  lastMessageInArr.id > 0 &&
  lastMessageInArr?.creation_uuid === lastMessage?.creation_uuid;

export const getMessageHashFromUrl = () =>
  (window.location.hash || "").split("#message_")[1];

export const showDateChangeLine = (
  displayDayChange: boolean,
  dayChange: boolean,
) => displayDayChange && dayChange;

export interface ShowUnreadMessageLineProps {
  disableUnreadMessageLine?: boolean;
  id?: string;
  isPinned?: boolean;
  unreadMessageId?: number | null;
}

export const showUnreadMessageLine = ({
  id,
  isPinned,
  disableUnreadMessageLine,
  unreadMessageId,
}: ShowUnreadMessageLineProps) =>
  !isNull(id) &&
  !isPinned &&
  !disableUnreadMessageLine &&
  // eslint-disable-next-line sonarjs/different-types-comparison -- Disabled for plugin version update. Fix when possible.
  unreadMessageId === id;

interface ShouldHightlightMessageProps {
  id?: string;
  messageHashId?: string;
  messageHighlightId?: string;
}

export const shouldHightlightMessage = ({
  id,
  messageHashId,
  messageHighlightId,
}: ShouldHightlightMessageProps) =>
  (messageHashId && id == messageHashId) ||
  (messageHighlightId && id == messageHighlightId);

interface ShouldHightlightThreadMessageProps {
  id?: string;
  isParentMessage?: boolean;
  messageHighlightId?: string;
}

export const shouldHightlightThreadMessage = ({
  id,
  messageHighlightId,
  isParentMessage,
}: ShouldHightlightThreadMessageProps) =>
  !isParentMessage && messageHighlightId && id == messageHighlightId;

export const getDeleteConfirmationMessageText = (repliesCount = 0) => {
  if (repliesCount > 0) {
    return `${t("messaging.delete_message.description")} ${t(
      "messaging.delete_message.threads",
    )}`;
  }
  return t("messaging.delete_message.description");
};

export const getChatRoomUrl = (chatRoom: any, message: any) => {
  if (isEmpty(chatRoom)) return "";
  const { embedded_space, uuid } = chatRoom;
  const { slug } = embedded_space || {};
  const { embedded } = message;

  return embedded ? chatSpacePath({ spaceSlug: slug }) : chatRoomPath({ uuid });
};

export const getChatRoomMessageRedirectionHash = (message: any) =>
  `message_${message.id}`;

export const getChatThreadMessageRedirectionHash = (message: any) =>
  `message_${message.parent_message_id}`;

export const getChatRoomMessageRedirectionUrl = (
  chatRoom: any,
  message: any,
) => {
  const chatRoomUrl = getChatRoomUrl(chatRoom, message);
  return `${chatRoomUrl}#message_${message.id}`;
};

export const findMissingParticipantIds = (
  messages: any[],
  participants: any[],
) => {
  const missingParticipantIds: any[] = [];

  // eslint-disable-next-line sonarjs/array-callback-without-return, sonarjs/no-ignored-return -- Disabled for plugin version update. Fix when possible.
  messages.map(message => {
    const { chat_room_participant_id: participantId } = message;

    const sender = participants.find(
      participant => participant?.id === participantId,
    );

    if (!sender && !missingParticipantIds.includes(participantId))
      missingParticipantIds.push(participantId);
  });

  return missingParticipantIds;
};

export const isDirectChatRoom = (chatRoom: any) =>
  Boolean(chatRoom?.chat_room_kind === DIRECT_CHAT_ROOM);

export const isChatRoomAdminOrModerator = (chatRoom: any) => {
  const { current_participant: currentParticipant = {} } = chatRoom || {};
  return Boolean(currentParticipant?.admin || currentParticipant?.moderator);
};

export const getPortalTargetId = (
  isMessagingPage: boolean,
  isRenderedOnHeader: boolean,
) => {
  let portalTargetId: string | null = null;

  if (isMessagingPage) {
    portalTargetId = "fullscreen-modal-body";
  } else if (isRenderedOnHeader) {
    portalTargetId = "chat-threads-header";
  }

  return portalTargetId;
};
